import React, { useEffect, useRef } from 'react';

export default function Lightbox({ images = [], currentIndex = 0, isOpen = false, onClose, onPrev, onNext }) {
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    }

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose, onPrev, onNext]);

  if (!isOpen || images.length === 0) return null;

  const currentImg = images[currentIndex];

  function handleTouchStart(e) {
    touchStartX.current = e.targetTouches[0].clientX;
  }

  function handleTouchMove(e) {
    touchEndX.current = e.targetTouches[0].clientX;
  }

  function handleTouchEnd() {
    if (touchStartX.current - touchEndX.current > 50) {
      // Swiped left -> Next image
      onNext();
    }
    if (touchEndX.current - touchStartX.current > 50) {
      // Swiped right -> Prev image
      onPrev();
    }
  }

  return (
    <div 
      className="fixed inset-0 z-[999] bg-black/95 backdrop-blur-lg flex items-center justify-center select-none"
      onClick={onClose}
    >
      {/* Luk-knap */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 w-12 h-12 rounded-full bg-slate-900/80 border border-pink-500/40 text-white flex items-center justify-center hover:bg-pink-600 transition-colors"
        aria-label="Luk billede"
      >
        <i className="fa-solid fa-xmark text-xl" aria-hidden="true"></i>
      </button>

      {/* Tæller */}
      <div className="absolute top-5 left-6 text-xs sm:text-sm font-mono text-slate-300 bg-slate-900/80 px-3 py-1.5 rounded-full border border-pink-500/30">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Forrige knap */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          className="absolute left-2 sm:left-6 z-10 w-12 h-12 rounded-full bg-slate-900/80 border border-pink-500/40 text-white flex items-center justify-center hover:bg-pink-600 transition-colors"
          aria-label="Forrige billede"
        >
          <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true"></i>
        </button>
      )}

      {/* Billede Container */}
      <div 
        className="max-w-[92vw] max-h-[85vh] p-2 flex items-center justify-center relative"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={currentImg}
          alt={`ØrBike stemningsbillede ${currentIndex + 1}`}
          className="max-h-[82vh] max-w-full object-contain rounded-lg shadow-[0_0_50px_rgba(255,42,141,0.25)] border border-pink-500/20 animate-in zoom-in-95 duration-200"
        />
      </div>

      {/* Næste knap */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-2 sm:right-6 z-10 w-12 h-12 rounded-full bg-slate-900/80 border border-pink-500/40 text-white flex items-center justify-center hover:bg-pink-600 transition-colors"
          aria-label="Næste billede"
        >
          <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true"></i>
        </button>
      )}
    </div>
  );
}
